import { z } from "zod";

import { SchedulingAction, type AppointmentType } from "@/shared/models/appointment";

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export const agentPatientSchema = z.object({
  id: z.string().min(1),
  givenName: z.string().min(1),
  familyName: z.string().min(1),
  dateOfBirth: z.string().date(),
});

export type Patient = z.infer<typeof agentPatientSchema>;

export enum PatientMatchMethod {
  EHR_ONLY = "EHR_ONLY",
  FUZZY_NAME_AND_DOB = "FUZZY_NAME_AND_DOB",
  NAME_AND_DOB = "NAME_AND_DOB",
  PHONE_AND_DOB = "PHONE_AND_DOB",
  PHONE_AND_NAME = "PHONE_AND_NAME",
}

export type CreatePatientFromIntakeResponse =
  | { status: "created"; patient: Patient }
  | { status: "carrier_not_accepted"; carrier: string };

export const agentCoverageSchema = z.object({
  payer: z.string().min(1),
  plan: z.string().nullable(),
});

export type Coverage = z.infer<typeof agentCoverageSchema>;

export const searchPatientsResponseSchema = z.object({
  patient: agentPatientSchema,
  matchMethod: z.nativeEnum(PatientMatchMethod),
  coverage: agentCoverageSchema.nullable(),
  // Default supports rolling deploys where Voice updates before the backend starts returning this field.
  hasReferral: z.boolean().default(false),
});

export type SearchPatientsResponseInput = z.input<typeof searchPatientsResponseSchema>;
export type SearchPatientsResponse = z.infer<typeof searchPatientsResponseSchema>;

export const patientMedicationSchema = z.object({
  medicationId: z.string().min(1),
  medicationEntryId: z.string().min(1).nullable(),
  name: z.string().min(1),
  status: z.string().min(1).nullable(),
  isDiscontinued: z.boolean(),
  isSafeToRenew: z.boolean().nullable(),
  quantity: z.string().min(1).nullable(),
  quantityUnit: z.string().min(1).nullable(),
  route: z.string().min(1).nullable(),
  unstructuredSig: z.string().min(1).nullable(),
  structuredSig: z
    .object({
      action: z.string().min(1).nullable(),
      quantityValue: z.string().min(1).nullable(),
      quantityUnit: z.string().min(1).nullable(),
      route: z.string().min(1).nullable(),
      frequencyValue: z.string().min(1).nullable(),
      frequencyUnit: z.string().min(1).nullable(),
      frequencyDescription: z.string().min(1).nullable(),
      additionalInstructions: z.string().min(1).nullable(),
    })
    .nullable(),
  prescribedBy: z.string().min(1).nullable(),
  pharmacy: z.string().min(1).nullable(),
});

export type PatientMedication = z.infer<typeof patientMedicationSchema>;

export const getPatientMedicationsResponseSchema = z.object({
  medications: z.array(patientMedicationSchema),
  lastUpdated: z.string().min(1).nullable(),
  noMedicationsReported: z.boolean(),
});

export type GetPatientMedicationsResponse = z.infer<typeof getPatientMedicationsResponseSchema>;

export const patientNotFoundResponseSchema = z.object({
  error: z.string().min(1),
  code: z.literal("patient_not_found"),
});

export interface OpenAppointment {
  appointmentId: string;
  appointmentType: string;
  appointmentTypeId: string;
  date: string;
  startTime: string;
  duration: number;
  departmentId: string;
  providerId: string;
  patientAppointmentTypeName?: string;
}

export interface SearchOpenAppointmentsResponse {
  appointments: OpenAppointment[];
}

export interface ConfirmedAppointment {
  appointmentId: string;
  /** Internal Valerie appointment row id (for post-booking workflows). */
  valerieAppointmentId?: string;
  appointmentType?: string;
  appointmentTypeId?: string;
  dateTime: string;
  duration?: number;
  departmentId: string;
  /** Internal Valerie location ID. `departmentId` remains the EHR-facing location identifier. */
  locationId?: string | null;
  locationName?: string | null;
  providerId?: string;
  providerDisplayName: string | null;
  patientId: string;
  status?: string;
}

export interface BookAppointmentResponse {
  confirmedAppointment: ConfirmedAppointment & {
    locationId: string;
    locationName: string;
  };
}

/** Recoverable scheduling conflict returned when an offered slot is no longer bookable. */
export const APPOINTMENT_SLOT_NOT_AVAILABLE_ERROR = "appointment_slot_not_available";
export const APPOINTMENT_UNIT_STATE_UNCERTAIN_ERROR = "appointment_unit_state_uncertain";

export interface CancelAppointmentResponse {
  cancelledAppointment: ConfirmedAppointment;
}

export interface BookedAppointment {
  id: string;
  appointmentDatetime: string;
  type: string;
  reason: string | null;
  status: string | null;
  ehrId: string;
  /** Signed application-owned action reference. Callers must store it opaquely and keep it from the model. */
  appointmentActionId?: string;
  practitionerId: string | null;
  practitionerFirstName: string | null;
  practitionerLastName: string | null;
  practitionerDisplayName: string | null;
  durationMinutes?: number | null;
  locationId?: string | null;
  locationName?: string | null;
  // Actions the agent may take on THIS appointment (VAL-2214). Present only for groups that restrict
  // modification by resource; when set, the agent must not reschedule/cancel unless the action is
  // listed here (confirm is always listed). Undefined = no per-appointment restriction.
  allowedActions?: SchedulingAction[];
  /**
   * Actions whose visit category was authoritatively allowed by configured appointment policy.
   * Runtime orchestration may trust this instead of reclassifying lossy type/reason text.
   */
  configuredPolicyAuthorizedActions?: SchedulingAction[];
  /** Backend-authored atomic reschedule unit. IDs are Valerie appointment IDs, not EHR credentials. */
  rescheduleUnit?: {
    appointmentIds: [string, string];
    maxGapMinutes: number;
    sameLocation: boolean;
    preserveMemberOrder: boolean;
    /** Backend-authored scheduling strategy for this member of the unit. */
    targetStrategy: "eligible_provider" | "preserve_source_resource";
  };
}

export interface GetBookedAppointmentsResponse {
  appointments: BookedAppointment[];
  searchedDateRanges: string[];
}

export interface AvailabilityAppointmentSlot {
  appointmentId: string;
  /** Opaque integrity capability. Keep it out of model-visible tool results and return it unchanged on mutation. */
  appointmentSlotActionId?: string;
  appointmentType: string;
  ehrAppointmentTypeId: string | null;
  start: string;
  end: string;
  practitionerId: string | null;
  practitionerFirstName: string | null;
  practitionerLastName: string | null;
  practitionerDisplayName: string | null;
  locationId: string | null;
  locationName: string | null;
}

export interface AvailabilityResponse {
  slots: AvailabilityAppointmentSlot[];
  searchedDateRanges: string[];
}

export interface Location {
  locationId: string;
  locationName: string;
  /** EHR department/location id used only for trusted inbound-site scoping. */
  locationEhrId?: string | null;
}

export interface GetLocationsResponse {
  locations: Location[];
}

export interface RescheduleAppointmentResponse {
  rescheduledAppointment: BookedAppointment;
}

const bookedAppointmentSchema = z.object({
  id: z.string().min(1),
  appointmentDatetime: z.string().min(1),
  type: z.string().min(1),
  reason: z.string().nullable(),
  status: z.string().nullable(),
  ehrId: z.string().min(1),
  appointmentActionId: z.string().min(1).optional(),
  practitionerId: z.string().min(1).nullable(),
  practitionerFirstName: z.string().nullable(),
  practitionerLastName: z.string().nullable(),
  practitionerDisplayName: z.string().nullable(),
  durationMinutes: z.number().int().positive().nullable().optional(),
  locationId: z.string().min(1).nullable().optional(),
  locationName: z.string().nullable().optional(),
  allowedActions: z.array(z.nativeEnum(SchedulingAction)).optional(),
});

export const rescheduleAppointmentUnitResponseSchema = z.object({
  moves: z
    .array(
      z.object({
        previousAppointmentId: z.string().min(1),
        appointment: bookedAppointmentSchema,
      })
    )
    .length(2),
});

export type RescheduleAppointmentUnitResponse = z.infer<typeof rescheduleAppointmentUnitResponseSchema>;

export enum RecommendationType {
  ANY = "any",
  ESTABLISHED_PATIENT = "establishedPatientScheduling",
  NEW_PATIENT = "newPatientScheduling",
  RESCHEDULING = "rescheduling",
}

export interface EligibilityProviderResult {
  providerName: string;
  practitionerId: string;
  isEligible: boolean;
  reasons: string[];
  agentInstructions?: string;
  // The patient's soonest existing upcoming appointment, when they already have one
  // can be combined with agentInstructions to inform agent how to use this data
  upcomingAppointment?: { date: string; providerName: string };
  nextAppointmentDetails?: {
    approximateDate: string;
    appointmentType?: AppointmentType | null;
  };
}

export interface CheckEligibilityResponse {
  providerEligibilityResults: EligibilityProviderResult[];
  reason?: string | null;
  diagnosis_list?: Array<{
    diagnosis_text: string | null;
  }> | null;
}

export interface EligibilityLocationResult {
  locationName: string | null;
  locationId: string;
  isEligible: boolean;
  reasons: string[];
  agentInstructions?: string;
}

/**
 * Output of the agentic eligibility sub-agent: a flat, isEligible-flagged determination of which
 * candidate providers/locations the patient may be scheduled with for the given scenario. Consumed
 * by the ranking sub-agent (which uses only the isEligible entries).
 */
export interface AgenticEligibilityResponse {
  recommendationType: RecommendationType;
  providerResults: EligibilityProviderResult[];
  locationResults: EligibilityLocationResult[];
}

/** A single ranked open slot. appointmentSlot is the verbatim slot returned by getProviderAvailability. */
export interface RankedAppointment {
  rank?: number | null;
  providerName?: string | null;
  locationName?: string | null;
  reasoning?: string | null;
  appointmentSlot: AvailabilityAppointmentSlot;
}

/** Why the ranking sub-agent produced zero recommendations. Mutually exclusive with a non-empty list. */
export interface RankingNoRecommendationsReason {
  category: "no_open_slots" | "no_eligible_providers_or_locations";
  explanation: string;
}

/**
 * Output of the agentic ranking sub-agent: the ranked open appointments for the patient. Exactly one
 * of `recommendations` (non-empty) / `noRecommendationsReason` (non-null) is populated per response.
 */
export interface AgenticRankedAppointmentsResponse {
  recommendations: RankedAppointment[];
  noRecommendationsReason: RankingNoRecommendationsReason | null;
}
